import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '../../environments/environment';
import { ConnectivityService } from './connectivity.service';

interface SecurityEvent {
  eventType: string;
  occurredAt: string;
  details: Record<string, unknown>;
}

const SECURITY_EVENT_QUEUE_KEY = 'tops.security.eventQueue';

@Injectable({ providedIn: 'root' })
export class SecurityEventService {
  private readonly http = inject(HttpClient);
  private readonly connectivity = inject(ConnectivityService);
  private flushing = false;

  constructor() {
    this.connectivity.isOnline$.subscribe((isOnline) => {
      if (isOnline) this.flushQueuedEvents();
    });
  }

  record(eventType: string, details: Record<string, unknown> = {}): void {
    const event: SecurityEvent = {
      eventType,
      occurredAt: new Date().toISOString(),
      details,
    };

    if (!this.connectivity.currentOnline) {
      this.queue(event);
      return;
    }

    this.forward(event, () => this.queue(event));
  }

  flushQueuedEvents(): void {
    if (this.flushing) return;

    const events = this.readQueue();
    if (!events.length) return;

    this.flushing = true;
    this.writeQueue([]);
    events.forEach((event) => this.forward(event, () => this.queue(event)));
    this.flushing = false;
  }

  private forward(event: SecurityEvent, onFailure: () => void): void {
    this.http.post(`${environment.backendApiBaseUrl}/security-events`, event).subscribe({
      error: onFailure,
    });
  }

  private queue(event: SecurityEvent): void {
    this.writeQueue([...this.readQueue(), event]);
  }

  private readQueue(): SecurityEvent[] {
    try {
      const rawQueue = localStorage.getItem(SECURITY_EVENT_QUEUE_KEY);
      const queue = rawQueue ? JSON.parse(rawQueue) : [];
      return Array.isArray(queue) ? queue as SecurityEvent[] : [];
    } catch {
      return [];
    }
  }

  private writeQueue(events: SecurityEvent[]): void {
    localStorage.setItem(SECURITY_EVENT_QUEUE_KEY, JSON.stringify(events));
  }
}